/**
 * DealSidebar — searchable, filterable list of deals.
 *
 * Filters applied here (text + verdict) so the map gets the same pre-filtered
 * set the user sees in the list. Inspector + zone filters live one level up
 * (they're shared across the whole dashboard).
 *
 * Virtualisation is intentionally deferred — Phase 5 isn't expected to push
 * past a few hundred rows. When a tenant's deal volume crosses ~500 we'll
 * swap the inner `.map()` for `react-virtual` without changing the API.
 */

import { useMemo, useState } from 'react';
import { Search, Loader2, MapPin, RefreshCw, ChevronDown, Users } from 'lucide-react';
import { clsx } from 'clsx';
import type { DealWithSuburb } from '../../hooks/useDealsWithSuburbs';
import type { LeadWithSuburb } from '../../hooks/useLeadsWithSuburbs';
import { verdictBucket, verdictColour } from '../../utils/regionValidation';
import { hueFromId, monogram } from './InspectorChips';
import { LEAD_VIOLET, LEAD_TO_BOOK_VIOLET, leadDisplayName } from '../../lib/leadDisplay';

type VerdictFilter = 'all' | 'approved' | 'review' | 'outside';

interface Props {
  items: DealWithSuburb[];
  leads?: LeadWithSuburb[];
  selectedDealId: string | null;
  selectedLeadId?: string | null;
  onSelectDeal: (dealId: string) => void;
  onSelectLead?: (leadId: string) => void;
  onRefresh: () => Promise<void>;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => Promise<void>;
}

export function DealSidebar({
  items,
  leads = [],
  selectedDealId,
  selectedLeadId = null,
  onSelectDeal,
  onSelectLead,
  onRefresh,
  loading,
  hasMore,
  onLoadMore,
}: Props) {
  const [search, setSearch] = useState('');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [leadsOpen, setLeadsOpen] = useState(true);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const { deal, regionMatch } = item;
      if (verdictFilter !== 'all') {
        if (verdictBucket(regionMatch?.verdict?.label) !== verdictFilter) return false;
      }
      if (q) {
        const hay = [deal.name, deal.address, regionMatch?.suburb, regionMatch?.zone]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, verdictFilter]);

  // Leads share the text search but not the verdict filter (verdicts are a
  // deal-pipeline concept; leads classify into lead / lead_to_book instead).
  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((item) => {
      const { lead, regionMatch } = item;
      const hay = [leadDisplayName(lead), lead.email, lead.address, regionMatch?.suburb, regionMatch?.zone]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [leads, search]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search deal name or address…"
            className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-1.5 pl-8 pr-3 text-xs text-neutral-100 placeholder:text-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="rounded-xl border border-white/10 bg-white/[0.04] p-1.5 text-neutral-400 hover:bg-white/[0.08] disabled:opacity-50"
          title="Refresh deals"
        >
          <RefreshCw className={clsx('h-3.5 w-3.5', refreshing && 'animate-spin')} />
        </button>
      </div>

      <div className="flex items-center gap-1.5 text-[11px]">
        {(['all', 'approved', 'review', 'outside'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVerdictFilter(v)}
            className={clsx(
              'rounded-full border px-2 py-0.5 capitalize transition',
              verdictFilter === v
                ? 'border-white/20 bg-white/10 text-white'
                : 'border-white/10 bg-white/[0.02] text-neutral-400 hover:bg-white/[0.06]',
            )}
          >
            {v === 'all' ? 'All' : v}
          </button>
        ))}
        <div className="ml-auto text-neutral-500 tabular-nums">{filtered.length}/{items.length}</div>
      </div>

      <div className="-mx-1 flex-1 overflow-y-auto px-1">
        {loading && items.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Fetching deals…</span>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-1 px-4 text-center text-neutral-500">
            <span className="text-xs">No deals from the CRM yet.</span>
            <span className="text-[11px] text-neutral-600">Connect your CRM in Setup to start.</span>
          </div>
        )}

        {filtered.length === 0 && items.length > 0 && (
          <div className="px-2 py-6 text-center text-xs text-neutral-500">
            Nothing matches that filter.
          </div>
        )}

        <ul className="space-y-1.5">
          {filtered.map((item) => {
            const { deal, regionMatch, assignedInspector, lat, lng, loadingRegion } = item;
            const isSelected = deal.id === selectedDealId;
            const pinColour = verdictColour(regionMatch?.verdict?.label ?? null);
            const insColour = assignedInspector ? hueFromId(assignedInspector.id) : '#475569';
            return (
              <li key={deal.id}>
                <button
                  type="button"
                  onClick={() => onSelectDeal(deal.id)}
                  className={clsx(
                    'group relative w-full rounded-xl border px-3 py-2 text-left transition',
                    isSelected
                      ? 'border-brand-500/40 bg-brand-500/10'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.05]',
                  )}
                >
                  <div className="flex items-start gap-2.5">
                    <span
                      className="mt-1 h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: pinColour, boxShadow: `0 0 0 2px ${pinColour}22` }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div className="truncate text-sm font-medium text-neutral-100">{deal.name}</div>
                        {lat == null && (
                          <span className="rounded bg-amber-500/15 px-1 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-300">
                            no coords
                          </span>
                        )}
                      </div>
                      {deal.address && (
                        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-neutral-500">
                          <MapPin className="h-2.5 w-2.5" />
                          <span className="truncate">{deal.address}</span>
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-1.5 text-[10px]">
                        {loadingRegion ? (
                          <span className="text-neutral-600">Locating…</span>
                        ) : regionMatch?.zone ? (
                          <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-neutral-300">
                            {regionMatch.zone}
                          </span>
                        ) : (
                          <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-red-300">
                            Out of zones
                          </span>
                        )}
                        {regionMatch?.verdict?.label && (
                          <span
                            className="rounded px-1.5 py-0.5 font-medium"
                            style={{
                              backgroundColor: `${pinColour}22`,
                              color: pinColour,
                            }}
                          >
                            {regionMatch.verdict.label}
                          </span>
                        )}
                      </div>
                    </div>
                    {assignedInspector ? (
                      <span
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                        style={{ backgroundColor: insColour }}
                        title={assignedInspector.name}
                      >
                        {monogram(assignedInspector.name)}
                      </span>
                    ) : (
                      <span
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-white/15 text-[10px] text-neutral-600"
                        title="Unassigned"
                      >
                        ?
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>

        {hasMore && filtered.length > 0 && (
          <button
            type="button"
            onClick={() => void onLoadMore()}
            className="mt-2 w-full rounded-xl border border-dashed border-white/10 bg-white/[0.02] py-2 text-xs text-neutral-400 hover:bg-white/[0.05]"
          >
            Load more
          </button>
        )}

        {/* Leads section — collapsible remarketing + book-worthy pool. */}
        {leads.length > 0 && (
          <div className="mt-4 border-t border-white/5 pt-3">
            <button
              type="button"
              onClick={() => setLeadsOpen((o) => !o)}
              className="flex w-full items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-neutral-400 hover:text-neutral-200"
            >
              <ChevronDown className={clsx('h-3 w-3 transition', !leadsOpen && '-rotate-90')} />
              <Users className="h-3 w-3" style={{ color: LEAD_VIOLET }} />
              Leads
              <span
                className="ml-1 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums"
                style={{ backgroundColor: `${LEAD_VIOLET}22`, color: LEAD_VIOLET }}
              >
                {filteredLeads.length}
              </span>
            </button>

            {leadsOpen && (
              <ul className="mt-2 space-y-1.5">
                {filteredLeads.length === 0 && (
                  <li className="px-2 py-4 text-center text-[11px] text-neutral-600">No leads match that search.</li>
                )}
                {filteredLeads.map((item) => {
                  const { lead, regionMatch, leadStatus, loadingRegion } = item;
                  const isSelected = lead.id === selectedLeadId;
                  const accent = leadStatus === 'lead_to_book' ? LEAD_TO_BOOK_VIOLET : LEAD_VIOLET;
                  return (
                    <li key={lead.id}>
                      <button
                        type="button"
                        onClick={() => onSelectLead?.(lead.id)}
                        className={clsx(
                          'group w-full rounded-xl border px-3 py-2 text-left transition',
                          isSelected
                            ? 'border-violet-400/40 bg-violet-500/10'
                            : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.05]',
                        )}
                      >
                        <div className="flex items-start gap-2.5">
                          <span className="mt-1 h-2 w-2 shrink-0 rotate-45" style={{ backgroundColor: accent, boxShadow: `0 0 0 2px ${accent}22` }} />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-neutral-100">{leadDisplayName(lead)}</div>
                            {lead.email && <div className="mt-0.5 truncate text-[11px] text-neutral-500">{lead.email}</div>}
                            {lead.address && (
                              <div className="mt-0.5 flex items-center gap-1 text-[11px] text-neutral-500">
                                <MapPin className="h-2.5 w-2.5" />
                                <span className="truncate">{lead.address}</span>
                              </div>
                            )}
                            <div className="mt-1 flex items-center gap-1.5 text-[10px]">
                              <span
                                className="rounded px-1.5 py-0.5 font-medium"
                                style={{ backgroundColor: `${accent}22`, color: accent }}
                              >
                                {leadStatus === 'lead_to_book' ? 'Lead to book' : 'Lead'}
                              </span>
                              {loadingRegion ? (
                                <span className="text-neutral-600">Locating…</span>
                              ) : regionMatch?.zone ? (
                                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-neutral-300">{regionMatch.zone}</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
